import * as Sentry from '@sentry/react'
import { logger } from '@/core/logger'
import { env as firebaseEnv } from '@/core/firebase/config'
import { APP_VERSION } from '@/core/version/version'

// Re-exported so existing `import { APP_VERSION } from '@/core/sentry'`
// callers keep working — single source of truth lives in `core/version/version.ts`.
export { APP_VERSION }

/**
 * Sentry `environment` field. We resolve from the Firebase env first so the
 * tag matches the backend project users are actually hitting — a staging
 * build deployed to the prod-hostname would otherwise report under the
 * wrong bucket.
 */
export const SENTRY_ENV =
  import.meta.env.VITE_SENTRY_ENV ?? firebaseEnv ?? (import.meta.env.PROD ? 'prod' : 'dev')

/**
 * Initialise Sentry when a DSN is provided via build-time env.
 *
 * DSN is public by design — safe to include in shipped bundles, and gated by
 * project settings on the Sentry dashboard. Leave `VITE_SENTRY_DSN` empty in
 * dev if you want to avoid noise during local work.
 *
 * Source maps are uploaded at build time by `@sentry/vite-plugin` — see
 * `vite.config.ts`. Runtime init only needs DSN + environment + release tag.
 *
 * Tags + context set here (on top of the built-in `environment` + `release`)
 * so every event carries the info you need to triage at a glance — "which
 * build", "which Firebase project", "which app" when we have more than one.
 */
export function initSentry(): void {
  // Always expose the test helpers — `Sentry.captureException` / `captureMessage`
  // are no-ops when Sentry isn't initialized, and `__throwTestError` only needs
  // the ErrorBoundary to verify locally. Registering them before the DSN gate
  // means `window.__throwTestError()` works in dev without a DSN too.
  registerTestHelpers()

  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    if (import.meta.env.PROD) {
      logger.w('Sentry: VITE_SENTRY_DSN is not set — errors will not be reported.')
    } else {
      logger.i(
        `Sentry disabled (no DSN) · env=${SENTRY_ENV} version=${APP_VERSION} — ` +
          '__throwTestError still works against the local ErrorBoundary.',
      )
    }
    return
  }

  Sentry.init({
    dsn,
    environment: SENTRY_ENV,
    release: APP_VERSION,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Network request failed',
      'Failed to fetch',
      // Stale-SW race during a deploy: the client had a cached reference to
      // the old `/assets/index-<hash>.js`; the server now responds with the
      // HTML shell. Self-heals on the next visit. Noise, not signal.
      // (Sentry CLUBMANAGER-PWA-6.)
      "'text/html' is not a valid JavaScript MIME type",
      'is not a valid JavaScript MIME type',
      // Cold-start of the analytics callable on a freshly-deployed function
      // — first invocation can hit a 5xx before the container is warm. The
      // app retries silently and recovers. (Sentry CLUBMANAGER-PWA-X.)
      'get_analytics callable not reachable yet',
      // Playground page intentionally throws to demo the error boundary.
      // (Sentry CLUBMANAGER-PWA-W.)
      /^Error:\s*forced$/,
      // Auth login: wrong password / unknown email. Expected user error,
      // surfaced inline on the login form. (Sentry CLUBMANAGER-PWA-1G.)
      'login failed auth/invalid-credential',
      'auth/invalid-credential',
      'auth/wrong-password',
      'auth/user-not-found',
      // Auth network blips: user offline, transient connectivity, server
      // bounced mid-handshake. Surfaced inline on the login form; not a bug.
      // (Sentry CLUBMANAGER-PWA-23.)
      'auth/network-request-failed',
      // Google sign-in popup dismissed by the user — they clicked away
      // from the Google chooser. Expected behaviour; the auth flow returns
      // and the form is ready for another attempt. (Sentry CLUBMANAGER-PWA-22.)
      'auth/popup-closed-by-user',
      // Magic-link redemption with an expired or already-used link. The
      // preview-claim flow renders an inline "link expired" panel and
      // offers a fresh one. Not a bug. (Sentry CLUBMANAGER-PWA-1X.)
      'Enlace no válido',
      'auth/invalid-action-code',
      // IndexedDB transient: browser kills the IDB connection (tab focus
      // change in a low-memory state, Safari ITP, etc.). The Firestore
      // SDK auto-reconnects on next read; users see one stuck spinner that
      // resolves on refresh. (Sentry CLUBMANAGER-PWA-21.)
      'Connection to Indexed Database server lost',
      'UnknownError: Connection to Indexed Database server lost',
      // Preview-claim flow: legitimate business-rule denial — only the
      // owner of a mockup club can convert it to a trial. The toast handles
      // it; nothing for an engineer to do. (Sentry CLUBMANAGER-PWA-1F.)
      'Sólo el propietario puede activar la prueba',
      // FCM noise: when an admin signs in without a staff doc on the
      // current club, we deliberately skip device registration and log a
      // warning. Not an error — informational. (Sentry CLUBMANAGER-PWA-1E.)
      'fcm token skipped: no staff doc',
      // Domain validation errors thrown by repositories. These are caught
      // by mutation onError handlers and rendered to the user inline (e.g.
      // "La fecha de nacimiento no puede ser futura.") — they aren't bugs,
      // they're expected user-input failures. Don't pollute Sentry.
      // (CLUBMANAGER-PWA-1Q + 1R + 1P.)
      'CrmPlayerInvalidDateOfBirthError',
      'La fecha de nacimiento no puede ser futura',
      // Booking conflicts: thrown when staff tries to create a booking on
      // a slot already taken. Surfaced inline on the booking form. Not a
      // bug. (CLUBMANAGER-PWA-1M + 1K.)
      'BookingConflictError',
      'Court already booked from',
      // Staff invite collision: typed business error thrown when the
      // owner tries to invite an email that's already on staff. The
      // invite dialog catches it and renders inline. Not a bug.
      // (Sentry CLUBMANAGER-PWA-1T.)
      'EmailAlreadyInUseError',
      'Email already in use by another staff member',
      // React HMR hook-order glitch on dev only: Vite re-runs a component
      // whose hook order changed since the last save and the next render
      // throws `Cannot read properties of null (reading 'getSnapshot')`
      // before the page hot-reloads. Never reaches prod bundles. (Sentry
      // CLUBMANAGER-PWA-18.)
      /Cannot read properties of null \(reading 'getSnapshot'\)/,
      // Firestore client shut down mid-navigation — happens when the FCM SW
      // unregisters during deploy (registerType: 'prompt' replaces SWs and
      // tears down the IDB connection). Self-heals on next page load. Noise.
      // (Sentry CLUBMANAGER-PWA-3C.)
      'Firestore shutting down',
      // Health-monitor noise: the app's own keep-alive pinger marks the host
      // as down when a deploy is propagating. The next ping succeeds. Not a
      // user-visible error. (Sentry CLUBMANAGER-PWA-2T.)
      /Downtime detected for /,
      // Google Maps loader transient — ad-blocker, offline, or Maps CDN blip.
      // The address autocomplete shows an inline "no maps available" fallback;
      // nothing to fix here. (Sentry CLUBMANAGER-PWA-2V.)
      'The Google Maps JavaScript API could not load',
      // Operational message thrown by the Stripe-setup CF when a club tries
      // to start a subscription before we've provisioned their Stripe price.
      // Surfaced inline with a contact CTA. Not a bug. (Sentry CLUBMANAGER-PWA-2Z.)
      'Los pagos online están en preparación',
      // Workbox-window internals: occasional unguarded `.waiting` access on a
      // registration that the runtime has already cleared. Origin is
      // `workbox-window/build/workbox-window.prod.es5.mjs`, not our code, and
      // affects 0 real users (probes/bots). Self-heals on next load.
      // (Sentry CLUBMANAGER-PWA-3R, 121×.)
      /Cannot read properties of undefined \(reading 'waiting'\)/,
    ],
    beforeSend(event, hint) {
      // Drop events from local `npm run dev` sessions. They're transient by
      // definition: HMR module ghosts, watcher race conditions, port-bouncing
      // login attempts, dev-only stale-export SyntaxErrors. We can't act on
      // them from production stacks, and they swamp the unresolved-issues
      // queue with churn that obscures real signal. The deployed dev project
      // (upadelmatch-club-dev.web.app) is unaffected — only loopback hosts
      // are filtered. (Sentry CLUBMANAGER-PWA-1X / 23 / 24 / 25 / 26 / 27 /
      // 28 / 29 / 2A–2S — 25 issues at a stroke.)
      if (typeof window !== 'undefined') {
        const host = window.location.hostname
        if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') {
          return null
        }
      }

      // Drop Firestore permission-denied events from EVERY source. These are
      // never actionable bugs:
      //  - Route gates already redirect non-matching roles before the page
      //    paints, so a denied query is the auth-race window between sign-in
      //    and the gate's first render.
      //  - QueryCache.onError already demotes them to `logger.d` and the UI
      //    falls back cleanly.
      //  - Genuine rules misconfiguration would surface in QA against our
      //    own admin/test accounts long before a real user hits it.
      // Previous catchall-only filter (CLUBMANAGER-PWA-3, 84→83 events)
      // missed paths where the source tag wasn't set — extending coverage
      // to all sources closes the remaining drip (12 users on /staff via
      // preview-session race). Catchall sources still listed for context.
      {
        const error = hint?.originalException
        const code = (error as { code?: string } | null)?.code
        const message = error instanceof Error ? error.message : ''
        const isFirestorePermDenied =
          code === 'permission-denied' ||
          /Missing or insufficient permissions/.test(message)
        if (isFirestorePermDenied) {
          return null
        }
      }

      // Drop Vite HMR ghosts: when the dev server hot-replaces a module that
      // renames or removes a top-level binding, the stale closure throws
      // `ReferenceError: <symbol> is not defined`. These never reach prod
      // bundles — the stack is rooted in `/@react-refresh` or
      // `vite/dist/client/`. (Sentry CLUBMANAGER-PWA-10/11/12/13/14/V/Y/Z.)
      // Defense-in-depth — the localhost gate above already drops these,
      // but a developer running `vite preview` against a production build
      // on an internal hostname would still emit them otherwise.
      if (SENTRY_ENV === 'dev') {
        const error = hint?.originalException
        const message = error instanceof Error ? error.message : ''
        if (/is not defined$/.test(message)) {
          const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? []
          const fromHmr = frames.some((f) => {
            const file = f.filename ?? f.abs_path ?? ''
            return file.includes('@react-refresh') || file.includes('vite/dist/client')
          })
          if (fromHmr) return null
        }
        // Same shape, different exception class: stale-export SyntaxError
        // when Vite serves a freshly-edited module whose exports no longer
        // match the importing closure. (Sentry CLUBMANAGER-PWA-25 / 26.)
        if (/does not provide an export named/.test(message)) {
          return null
        }
      }
      return event
    },
  })

  Sentry.setTags({
    app: 'clubmanager-pwa',
    environment: SENTRY_ENV,
    version: APP_VERSION,
    firebase_project: firebaseEnv,
  })
  Sentry.setContext('build', {
    app: 'clubmanager-pwa',
    version: APP_VERSION,
    environment: SENTRY_ENV,
    firebase_project: firebaseEnv,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  })

  logger.i(`Sentry ready · env=${SENTRY_ENV} version=${APP_VERSION}`)
}

/**
 * Stamp the signed-in user on every subsequent Sentry event. Mirrors
 * `setAnalyticsUser` so support can cross-reference an event in Sentry with
 * the same uid in GA4. Pass `null` on sign-out to clear; `Sentry.setUser`
 * accepts `null` and wipes the prior identity.
 *
 * Email + username give the support team a friendly label without leaving
 * Sentry — combined with the club tags below, an issue list reads as
 * "Antonio (Padel Sur, owner) · 5 events · auth/wrong-password" instead of
 * "anonymous · 5 events".
 */
export function setSentryUser(
  user: { uid: string; email: string | null; displayName: string | null } | null,
): void {
  if (user == null) {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({
    id: user.uid,
    ...(user.email != null && user.email.length > 0 ? { email: user.email } : {}),
    ...(user.displayName != null && user.displayName.length > 0
      ? { username: user.displayName }
      : {}),
  })
}

/**
 * Tag every subsequent Sentry event with the active club. Use:
 *   - `club_id` tag → filter the issue list to one tenant
 *   - `subscription_status` tag → split paying-club errors from trials
 *   - `is_partner_club` / `is_demo` tags → cut noise from founder/demo clubs
 *   - `club` context → richer info (name, slug, court count) shown when
 *     opening an event
 *
 * Pass `null` on sign-out / club switch to a no-club state — clears all of
 * the above. We intentionally leave `firebase_project` untouched because
 * that's set once at init and never changes during a session.
 */
export interface SentryClubInfo {
  id: string
  name?: string
  slug?: string
  /** Lifecycle status: mockup / trial / active / closed. */
  subscriptionStatus?: string
  /** Billing status: not_billable / current / past_due / exempt. */
  billingStatus?: string
  partnerClub?: boolean
  isDemo?: boolean
  activeCourtCount?: number
}

export function setSentryClub(club: SentryClubInfo | null): void {
  if (club == null) {
    Sentry.setTag('club_id', null)
    Sentry.setTag('subscription_status', null)
    Sentry.setTag('billing_status', null)
    Sentry.setTag('is_partner_club', null)
    Sentry.setTag('is_demo', null)
    Sentry.setContext('club', null)
    return
  }
  Sentry.setTag('club_id', club.id)
  if (club.subscriptionStatus) {
    Sentry.setTag('subscription_status', club.subscriptionStatus)
  }
  if (club.billingStatus) {
    Sentry.setTag('billing_status', club.billingStatus)
  }
  if (typeof club.partnerClub === 'boolean') {
    Sentry.setTag('is_partner_club', String(club.partnerClub))
  }
  if (typeof club.isDemo === 'boolean') {
    Sentry.setTag('is_demo', String(club.isDemo))
  }
  Sentry.setContext('club', {
    id: club.id,
    name: club.name ?? null,
    slug: club.slug ?? null,
    subscription_status: club.subscriptionStatus ?? null,
    billing_status: club.billingStatus ?? null,
    partner_club: club.partnerClub ?? null,
    is_demo: club.isDemo ?? null,
    active_court_count: club.activeCourtCount ?? null,
  })
}

/**
 * Tag every subsequent Sentry event with the user's club role. Useful for
 * triaging "this only happens to receptionists" patterns. `null` clears the
 * tag (e.g., admin-only sessions where no staff doc exists).
 */
export function setSentryRole(roleId: string | null): void {
  Sentry.setTag('role_id', roleId)
}

/**
 * Tag the session as a verified platform admin. Drives the `beforeSend`
 * filter that drops `permission-denied` noise emitted during admin-claim
 * redirect races (CLUBMANAGER-PWA-3 / -T / -3K cluster). Must mirror
 * `useIsAdmin()` exactly — platform_config membership AND verified email —
 * so employees logging in with a non-admin `@upadelmatch.com` test account
 * still see real permission-denied bugs.
 */
export function setSentryPlatformAdmin(isAdmin: boolean): void {
  Sentry.setTag('is_platform_admin', isAdmin ? 'true' : 'false')
}

/**
 * Expose a dead-simple way to verify the pipeline end-to-end from any build:
 * open devtools and run `window.__throwTestError()` or
 * `window.__captureTestEvent()`. Safe to ship — only fires on explicit
 * invocation. `captureMessage` is a no-op if Sentry wasn't initialised,
 * which makes the `throw` variant the one useful in dev.
 */
function registerTestHelpers(): void {
  if (typeof window === 'undefined') return
  const w = window as unknown as {
    __throwTestError?: () => void
    __captureTestEvent?: () => string | undefined
  }
  w.__throwTestError = () => {
    throw new Error(`Sentry test · ${SENTRY_ENV} · v${APP_VERSION} · ${new Date().toISOString()}`)
  }
  w.__captureTestEvent = () =>
    Sentry.captureMessage(
      `Sentry test · ${SENTRY_ENV} · v${APP_VERSION} · ${new Date().toISOString()}`,
      'info',
    )
}
